<template>
	<Route>
		<template slot="title">
			Dashboard
		</template>
		<v-container
			grid-list-xl
			my-2
		>
			<v-layout
				justify-center
				row
				wrap
			>
				<v-flex
					xs12
					xl8
				>
					<!-- <DashboardGuide :filteredRecentReports="filteredRecentReports" /> -->
					<v-container v-if="!isSingleProject">
						<v-card-title class="pa-0">
							<div class="headline">Recent Projects</div>
							<v-btn
								:to="{ name: 'ProjectsList' }"
								color="secondary"
							>
								All Projects
							</v-btn>
						</v-card-title>
						<v-layout
							row
							wrap
						>
							<v-flex v-if="recentProjects.length === 0">
								<p>No recent projects available.</p>
							</v-flex>
							<template v-else>
								<template v-for="(project, i) in recentProjects.models">
									<ProjectListAndCard
										v-if="i < numberOfModelsToShow"
										:key="project.id"
										:project="project"
										:card-type="cardType"
										@edit="setEditProject"
										@archive="archiveProject"
									/>
								</template>
							</template>
						</v-layout>
					</v-container>
					<v-container>
						<v-card-title class="pa-0">
							<div class="headline">Recent Reports</div>
						</v-card-title>
						<loading :loading="recentReports.loading" />
						<v-layout
							v-if="!recentReports.loading"
							row
							wrap
						>
							<v-flex v-if="recentReports.length === 0">
								<p>Nothing here yet. Create a report to get started.</p>
							</v-flex>
							<template v-else>
								<template v-for="(report, i) in recentReports.models">
									<ReportListAndCard
										v-if="i < numberOfModelsToShow"
										:key="report.id"
										:report="report"
										:card-type="cardType"
										@edit="setEditReport"
										@archive="archiveReport"
									/>
								</template>
							</template>
						</v-layout>
					</v-container>
				</v-flex>
			</v-layout>
		</v-container>
		<v-dialog
			v-model="editReport.show"
			max-width="800px"
		>
			<v-card v-if="editReport.show">
				<EditReport
					:edit-report="editReport.report"
					@saving-done="editReport.show = false"
					@cancel="editReport.show = false"
				/>
			</v-card>
		</v-dialog>
		<v-dialog
			ref="dialog"
			v-model="editProject.show"
			max-width="800px"
		>
			<v-card v-if="editProject.show">
				<EditProject
					:edit-project="editProject.project"
					@saving-done="editProject.show = false"
					@cancel="editProject.show = false"
				/>
			</v-card>
		</v-dialog>
		<Confirm ref="confirm" />
	</Route>
</template>

<script>
import Route from '@/views/layouts/Route';
import ReportListAndCard from '@/components/Report/ReportListAndCard';
import ProjectListAndCard from '@/components/Project/ProjectListAndCard';
import EditReport from '@/components/Report/EditReport';
import EditProject from '@/components/Project/EditProject';
import Confirm from '@/components/Confirm';
import { Projects } from '@/models/Project';
import { RecentReports } from '@/models/Report';

// Not used, moved to component, check for errors when importing
// import DashboardGuide from '@/components/DashboardGuide';

export default {
	components: {
		Route,
		ReportListAndCard,
		ProjectListAndCard,
		EditReport,
		EditProject,
		Confirm,
	},
	data() {
		return {
			recentProjects: new Projects(),
			recentReports: new RecentReports(),

			editReport: { show: false, report: null },
			editProject: { show: false, project: null },

			cardType: this.$ls.get('cardStyle', 0),
			userId: this.$ls.get('id', null)
		};
	},
	created() {
		// Get recent projects from all projects available, based on localStorage recent projects value
		this.recentProjects = this.$store.get('projects/recent');
		// Fetch recent reports from backend, based on 'last_active' field
		this.recentReports.fetch().then().catch((e) => {
			dump('Error fetching recent reports', e);
			this.$notify('error', 'Failed to retrieve recent Reports.');
		});
	},
	computed: {
		isSingleProject() {
			return this.$store.get('projects/isSingleProject');
		},
		projects() {
			return this.$store.get('projects/active');
		},
		numberOfModelsToShow() {
			const lookup = {
				'xs': 3,
				'sm': 4,
				'md': 3,
				'lg': 4,
				'xl': 4,
			};

			return lookup[this.$vuetify.breakpoint.name] || 3; // 3 is default
		},
	},
	methods: {
		/**
		 * Duplicate in @/views/projects/ProjectsList.vue
		 * @todo Find a way to put it in one place
		 */
		archiveProject(project) {
			const name = project.name;
			const id = project.id;

			this.$refs.confirm.createConfirm(`This will archive "${name}".`, 'error')
				.then((answer) => {
					if (!answer) {
						return;
					}

					project.isArchived = true;
					project.save().then(() => {
						// Remove from current collection
						this.recentProjects.remove(project);
						// Archive
						this.$store.dispatch('projects/archive', project);
						// Refresh recent reports, in case some reports were in archived project
						/** @todo Refetch recent projects only when some of them were in archived project */
						this.recentReports.fetch().catch((e) => {
							dump('Error fetching recent reports', e);
							this.$notify('error', 'Failed to retrieve recent Reports.');
						});
						this.$notify('success', `Project "${name}" has been archived.`);
					}).catch((e) => {
						dump(e);
						this.$notify('error', `Failed to archive project "${name}".`);
					});
				});
		},
		/**
		 * Duplicate in @/views/project/ReportsList.vue
		 * @todo Find a way to put it in one place
		 */
		archiveReport(report) {
			const name = report.displayName;

			this.$refs.confirm.createConfirm(`This will archive "${name}".`, 'error')
				.then((answer) => {
					if (!answer) {
						return;
					}

					report.isArchived = true;
					report.save().then(() => {
						this.$store.dispatch('reports/archive', report);
						this.recentReports.remove(report);
						this.$notify('success', `Report "${name}" has been archived.`);
					}).catch((e) => {
						dump('Error archiving report', e);
						this.$notify('error', `Failed to archive report "${name}".`);
					});
				});
		},
		setEditProject(project) {
			this.editProject.project = project;
			this.editProject.show = true;
		},
		setEditReport(report) {
			this.editReport.report = report;
			this.editReport.show = true;
		},
		// Settings skill to skip dashboard
		beforeRouteEnter(to, from, next) {
			next((vm) => {
				if (vm.$store.get('isSkilled', 'hide_dashboard')) {
					vm.$router.push({ name: 'ProjectsList' });
				}
			});
		},
	},
};
</script>

<style lang="scss" scoped>
.card--link {
	text-decoration: none;
}
</style>
